import { apiClient } from '@/auth';

export type CgmEntry = {
  recorded_time: string;
  glucose_value: number;
};

export type CgmDay = {
  date: string;
  entries: CgmEntry[];
};

/**
 * Per MNT swagger:18483 (`PatientCgmWholeSummaryResponse`) — the response
 * for `/cgm/patients/{id}/whole_summary`. Drives the Glucose Pattern
 * Summary (AGP percentile chart), Glucose Statistics table, and Time in
 * Range bar.
 */
export type GlucosePeriodStat = {
  time_of_day: string; // 'HH:MM'
  minutes_after_meal?: number;
  avg: number;
  count: number;
  p5: number;
  p25: number;
  p75: number;
  p95: number;
  unit?: string;
};

export type GlucoseTimeInRangeValue = {
  target_name: 'low_danger' | 'low' | 'target' | 'high' | 'high_danger';
  mmol_lower: number;
  mmol_upper: number;
  minutes_in_range: number;
};

export type GlucoseTimeInRange = {
  minutes_total: number;
  values: GlucoseTimeInRangeValue[];
};

export type CgmWholeSummary = {
  date_since: string;
  date_until: string;
  glucose_period_stats?: GlucosePeriodStat[];
  bg_target_upper?: number;
  bg_target_lower?: number;
  glucose_time_in_range?: GlucoseTimeInRange;
  glucose_average?: number;
  glucose_std_dev?: number;
};

export type CgmDevice = {
  device_id: string;
  manufacturer?: string;
  model?: string;
  serial_number?: string | null;
  first_reading?: string | null;
  last_reading?: string | null;
};

export type CgmDevicesTable = {
  rows: CgmDevice[];
};

export const cgmApi = {
  async days(patientId: number) {
    const res = await apiClient.get<CgmDay[]>(`/patients/${patientId}/cgm/days`);
    return res.data;
  },
  /**
   * Raw glucose entries for a date range. Endpoint is
   * `GET /cgm/patients/{id}/cgm_data` (MNT swagger:8614) — note the
   * underscore: the route is `cgm_data`, not `cgm/data`. The previous V2
   * implementation was calling a non-existent `/patients/{id}/cgm/data`
   * which 404'd silently and surfaced as "no glucose data" everywhere
   * (Logs v2 glucose profile, Logs v3 daily glucose summary, etc.).
   *
   * Required query params per swagger are `time_since_local` /
   * `time_until_local` (ISO datetime, not bare YYYY-MM-DD), and the
   * response wraps the glucose array under `{ glucose: [...] }`. We
   * normalize back into V2's flat `CgmEntry[]` shape (renaming
   * `timestamp` → `recorded_time` and `value` → `glucose_value`) so all
   * existing consumers keep working unchanged.
   *
   * Server caps the response at 5,000 datapoints — single-day requests
   * are well within that. For multi-day ranges (CGM at 5-min intervals
   * = 288/day) the cap covers ~17 days.
   */
  async data(patientId: number, params?: { from?: string; to?: string }): Promise<CgmEntry[]> {
    if (!params?.from || !params?.to) return [];
    try {
      const res = await apiClient.get<{
        glucose?: Array<{ timestamp: string; value: number; unit?: string }>;
      }>(`/cgm/patients/${patientId}/cgm_data`, {
        params: {
          time_since_local: `${params.from}T00:00:00`,
          time_until_local: `${params.to}T23:59:59`,
        },
      });
      const glucose = Array.isArray(res.data?.glucose) ? res.data.glucose : [];
      return glucose.map((g) => ({
        recorded_time: g.timestamp,
        glucose_value: g.value,
      }));
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      // Patients without CGM consent / device → empty, not a hard error.
      if (status !== undefined && status >= 400 && status < 500) return [];
      throw err;
    }
  },

  /**
   * Aggregated CGM summary for a date range. Provides everything the
   * clinician summary needs: 24-hour percentile bands, time in range bands,
   * and global statistics (avg, std dev).
   * MNT swagger:8761.
   */
  async wholeSummary(
    patientId: number,
    params: { date_since: string; date_until: string },
  ): Promise<CgmWholeSummary | null> {
    try {
      const res = await apiClient.get<CgmWholeSummary>(
        `/cgm/patients/${patientId}/whole_summary`,
        { params },
      );
      return res.data;
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      // Patients without CGM consent or device 4xx — render an empty card.
      if (status !== undefined && status >= 400 && status < 500) return null;
      throw err;
    }
  },

  /**
   * List of CGM devices the patient has used. Drives the "Quick select"
   * dropdown — each device's `first_reading`/`last_reading` becomes a
   * one-click date range. MNT swagger:8709.
   */
  async devices(patientId: number): Promise<CgmDevicesTable> {
    try {
      const res = await apiClient.get<CgmDevicesTable>(`/cgm/patients/${patientId}/devices`);
      return res.data && Array.isArray(res.data.rows) ? res.data : { rows: [] };
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status !== undefined && status >= 400 && status < 500) return { rows: [] };
      throw err;
    }
  },
};
